import { apiClient } from "./client";
import type { ExpenseScope } from "./expenses";

export type TransportFleet = "buses" | "admin";

export interface TransportFuelRow {
    id: number;
    scope: ExpenseScope;
    storeName: string | null;
    fleet: TransportFleet;
    fuelDate: string;
    registrationNo: string;
    startMileage: number;
    endMileage: number;
    businessKm: number;
    fuelLiters: number;
    pricePerLiter: number;
    totalAmount: number;
    note: string | null;
}

export interface TransportDepreciationRow {
    id: number;
    scope: ExpenseScope;
    storeName: string | null;
    fleet: TransportFleet;
    periodYear: number;
    periodMonth: number;
    registrationNo: string;
    makeModel: string;
    bookValue: number;
    depreciationRatePercent: number;
    monthlyDepreciation: number;
    note: string | null;
}

export interface TransportLeasingRow {
    id: number;
    scope: ExpenseScope;
    storeName: string | null;
    fleet: TransportFleet;
    periodYear: number;
    periodMonth: number;
    registrationNo: string;
    makeModel: string;
    leaseAmount: number;
    leaseVat: number;
    totalAmount: number;
    note: string | null;
}

export interface TransportPeriodMeta {
    id: number;
    employeeName: string | null;
    approvedByName: string | null;
    preparedByName: string;
}

export interface TransportSummary {
    fuelBuses: number;
    fuelAdmin: number;
    fuelTotal: number;
    depreciationBuses: number;
    depreciationAdmin: number;
    depreciationTotal: number;
    leasingBuses: number;
    leasingAdmin: number;
    leasingTotal: number;
    grandTotal: number;
}

export interface TransportReportResponse {
    periodMeta: TransportPeriodMeta | null;
    fuelBuses: TransportFuelRow[];
    fuelAdmin: TransportFuelRow[];
    depreciationBuses: TransportDepreciationRow[];
    depreciationAdmin: TransportDepreciationRow[];
    leasingBuses: TransportLeasingRow[];
    leasingAdmin: TransportLeasingRow[];
    summary: TransportSummary;
    storeOptions: string[];
    tableReady: boolean;
    lastUpdatedAt: string;
}

export async function fetchTransportReport(params: {
    scope?: ExpenseScope;
    store?: string;
    year: number;
    month?: number;
}) {
    const { data } = await apiClient.get<TransportReportResponse>("/expenses/transport", {
        params: {
            scope: params.scope,
            store: params.store,
            year: params.year,
            month: params.month ?? 0,
        },
    });

    return data;
}

export async function saveTransportFuel(payload: Record<string, unknown>) {
    const { data } = await apiClient.put<TransportFuelRow>("/expenses/transport/fuel", payload);

    return data;
}

export async function deleteTransportFuel(id: number) {
    await apiClient.delete(`/expenses/transport/fuel/${id}`);
}

export async function saveTransportAsset(payload: Record<string, unknown>) {
    const { data } = await apiClient.put("/expenses/transport/assets", payload);

    return data;
}

export async function deleteTransportDepreciation(id: number) {
    await apiClient.delete(`/expenses/transport/depreciation/${id}`);
}

export async function deleteTransportLeasing(id: number) {
    await apiClient.delete(`/expenses/transport/leasing/${id}`);
}

export async function saveTransportPeriod(payload: Record<string, unknown>) {
    const { data } = await apiClient.put<TransportPeriodMeta>("/expenses/transport/period", payload);

    return data;
}

export async function fetchExpensePeriod(params: {
    scope?: ExpenseScope;
    store?: string;
    year: number;
    month: number;
}) {
    const { data } = await apiClient.get<{
        periodMeta: TransportPeriodMeta | null;
        storeOptions: string[];
    }>("/expenses/transport/period", {
        params: {
            scope: params.scope,
            store: params.store,
            year: params.year,
            month: params.month,
        },
    });

    return data;
}
